/**
 * Ported from sara/app `src/lib/hooks/use-input-type.ts` @ e8e8fecb4ec5.
 *
 * Bindery is canonical for this module from 2026-09-04; reconcile app-side
 * drift toward this copy.
 */

import { useState, useEffect, useRef } from "react";
import { useHoverCapability } from "./use-hover-capability";

/** Input type for user interaction */
export type InputType = "touch" | "mouse";

/**
 * Hook to detect and track the current input type (touch or mouse).
 *
 * Baseline comes from `(hover: hover)` via `useHoverCapability()` —
 * concurrent-safe through `useSyncExternalStore`. Touch/mousemove events
 * refine the reading in response to actual user interaction, with a delay
 * window to avoid touch-triggered synthetic mouse events flipping the mode.
 *
 * @returns Current input type ("touch" | "mouse")
 */

/**
 * Delay in ms to prevent false positives from touch events triggering mouse events.
 * Touch devices fire mouse events after touch events; this window ensures we don't
 * incorrectly switch back to "mouse" mode after a touch interaction.
 */
const TOUCH_TO_MOUSE_DELAY_MS = 500;

export function useInputType(): InputType {
  const hasHover = useHoverCapability();
  const [inputType, setInputType] = useState<InputType>(() =>
    hasHover ? "mouse" : "touch",
  );
  const lastTouchTimestampRef = useRef(0);

  // Re-baseline when the hover capability itself flips (e.g. a Bluetooth
  // mouse pairing with an iPad): the useState initializer only ran once,
  // and without this the mode would lag until the next matching event.
  // Adjusted during render rather than in an effect, which would paint the
  // stale mode first and then render again (react/set-state-in-effect).
  const [baselineHover, setBaselineHover] = useState(hasHover);
  if (hasHover !== baselineHover) {
    setBaselineHover(hasHover);
    setInputType(hasHover ? "mouse" : "touch");
  }

  useEffect(() => {
    const touchStartHandler = () => {
      setInputType("touch");
    };

    const updateTouchTimestamp = () => {
      lastTouchTimestampRef.current = Date.now();
    };

    const mouseHandler = () => {
      if (
        Date.now() - lastTouchTimestampRef.current >
        TOUCH_TO_MOUSE_DELAY_MS
      ) {
        setInputType("mouse");
      }
    };

    document.addEventListener("touchstart", touchStartHandler);
    document.addEventListener("touchstart", updateTouchTimestamp);
    document.addEventListener("mousemove", mouseHandler);

    return () => {
      document.removeEventListener("touchstart", touchStartHandler);
      document.removeEventListener("touchstart", updateTouchTimestamp);
      document.removeEventListener("mousemove", mouseHandler);
    };
  }, []);

  return inputType;
}
